import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, FolderKanban, ListChecks, PenLine, Plus } from 'lucide-react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { LoadError } from '../../../components/layout/LoadError';
import { EmptyState } from '../../../components/ui/EmptyState';
import { useDocKitBatch, useDocKitProfile, useDocuments } from '../../../hooks/doc-kit';
import type { BatchRow, DocumentRow, DocKitResponse } from '@payroll/shared';

const STATUS_LABEL: Record<DocumentRow['status'], string> = {
  draft: 'ร่าง',
  approved: 'ตรวจแล้ว',
  published: 'เผยแพร่แล้ว',
  archived: 'เก็บถาวร',
  cancelled: 'ยกเลิกแล้ว',
};

function isUnavailable<T extends object>(data: DocKitResponse<T> | undefined): data is { unavailable: string } {
  return Boolean(data && 'unavailable' in data);
}

function dateLabel(value: string): string {
  const date = new Date(value.length === 10 ? `${value}T00:00:00+07:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('th-TH', {
    timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: 'numeric',
  });
}

function latestBatchId(documents: DocumentRow[]): string | undefined {
  return [...documents].sort((a, b) => b.created_at.localeCompare(a.created_at))[0]?.batch_id;
}

function HomeSkeleton() {
  return (
    <div className="space-y-4 animate-pulse" aria-label="กำลังโหลดชุดเอกสารบริษัท">
      <div className="card p-5 space-y-3">
        <div className="h-6 w-2/5 rounded bg-gray-100" />
        <div className="h-4 w-4/5 rounded bg-gray-100" />
        <div className="h-11 w-48 rounded bg-gray-100" />
      </div>
      <div className="card p-5 space-y-3">
        <div className="h-5 w-1/3 rounded bg-gray-100" />
        <div className="h-16 rounded bg-gray-100" />
      </div>
    </div>
  );
}

function BatchProgress({ batch }: { batch: BatchRow }) {
  const counts = batch.counts;
  const total = Math.max(counts.total, 1);
  const completed = counts.pdf_ready;
  const width = Math.min(100, Math.max(0, Math.round((completed / total) * 100)));
  return (
    <section className="card p-4 space-y-3" aria-label="ความคืบหน้าชุดเอกสารล่าสุด">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-semibold">ชุดเอกสารล่าสุด</h2>
          <p className="text-sm text-gray-600 mt-1">มีผลตั้งแต่ {dateLabel(batch.effective_from)}</p>
        </div>
        <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">
          {batch.state === 'open' ? 'กำลังดำเนินการ' : batch.state === 'closed' ? 'เสร็จสิ้น' : 'ยกเลิกแล้ว'}
        </span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-gray-100" aria-label={`ความคืบหน้า ${completed} จาก ${counts.total}`}>
        <div className="h-full rounded-full bg-brand-600" style={{ width: `${width}%` }} />
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
        <div className="rounded-lg bg-gray-50 px-3 py-2"><span className="block text-xs text-gray-500">ทั้งหมด</span><strong>{counts.total}</strong></div>
        <div className="rounded-lg bg-gray-50 px-3 py-2"><span className="block text-xs text-gray-500">พร้อมตรวจ</span><strong>{counts.pdf_ready}</strong></div>
        <div className="rounded-lg bg-gray-50 px-3 py-2"><span className="block text-xs text-gray-500">ตรวจแล้ว</span><strong>{counts.approved}</strong></div>
        <div className="rounded-lg bg-gray-50 px-3 py-2"><span className="block text-xs text-gray-500">เผยแพร่แล้ว</span><strong>{counts.published}</strong></div>
      </div>
    </section>
  );
}

export function DocKitHome() {
  const navigate = useNavigate();
  const profileQuery = useDocKitProfile();
  const documentsQuery = useDocuments();
  const documents = !isUnavailable(documentsQuery.data) && documentsQuery.data ? documentsQuery.data.documents : [];
  const batchId = latestBatchId(documents);
  const batchQuery = useDocKitBatch(batchId);

  if (profileQuery.isLoading) {
    return <AdminLayout title="ชุดเอกสารบริษัท"><HomeSkeleton /></AdminLayout>;
  }
  if (profileQuery.error || !profileQuery.data) {
    return <AdminLayout title="ชุดเอกสารบริษัท"><LoadError error={profileQuery.error} onRetry={() => void profileQuery.refetch()} /></AdminLayout>;
  }
  if (isUnavailable(profileQuery.data)) {
    return <AdminLayout title="ชุดเอกสารบริษัท"><div className="card border-l-4 border-l-amber-400 p-4 text-sm">{profileQuery.data.unavailable}</div></AdminLayout>;
  }

  const profile = profileQuery.data.profile;
  if (!profile) {
    return (
      <AdminLayout title="ชุดเอกสารบริษัท" subtitle="รวมเอกสารที่องค์กรต้องจัดทำและติดตามการเผยแพร่">
        <EmptyState
          icon={FolderKanban}
          title="ยังไม่ได้เริ่มจัดชุดเอกสาร"
          detail="เลือกเอกสารที่ตรงกับองค์กร แล้วตอบข้อมูลที่จำเป็นก่อนสร้างร่างเอกสาร"
          action={{ label: 'เริ่มจัดชุดเอกสาร', onClick: () => navigate('/hr/doc-kit/select') }}
        />
      </AdminLayout>
    );
  }

  if (documentsQuery.isLoading || (batchId && batchQuery.isLoading)) {
    return <AdminLayout title="ชุดเอกสารบริษัท"><HomeSkeleton /></AdminLayout>;
  }
  if (documentsQuery.error || !documentsQuery.data) {
    return <AdminLayout title="ชุดเอกสารบริษัท"><LoadError error={documentsQuery.error} onRetry={() => void documentsQuery.refetch()} /></AdminLayout>;
  }
  if (isUnavailable(documentsQuery.data)) {
    return <AdminLayout title="ชุดเอกสารบริษัท"><div className="card border-l-4 border-l-amber-400 p-4 text-sm">{documentsQuery.data.unavailable}</div></AdminLayout>;
  }
  if (batchQuery.error || (batchId && !batchQuery.data)) {
    return <AdminLayout title="ชุดเอกสารบริษัท"><LoadError error={batchQuery.error} onRetry={() => void batchQuery.refetch()} /></AdminLayout>;
  }
  if (isUnavailable(batchQuery.data)) {
    return <AdminLayout title="ชุดเอกสารบริษัท"><div className="card border-l-4 border-l-amber-400 p-4 text-sm">{batchQuery.data.unavailable}</div></AdminLayout>;
  }

  const latestBatch = batchQuery.data?.batch;
  const continueStep = Math.min(6, Math.max(1, profile.wizard_step));
  return (
    <AdminLayout title="ชุดเอกสารบริษัท" subtitle="เลือก จัดทำ ตรวจ และเผยแพร่เอกสารขององค์กร">
      <div className="max-w-5xl space-y-4">
        <section className="card p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700"><FileText className="h-5 w-5" /></div>
            <div className="min-w-0">
              <h2 className="font-semibold">ข้อมูลชุดเอกสารพร้อมทำต่อ</h2>
              <p className="mt-1 text-sm text-gray-600">เลือกไว้ {profile.selected_codes.length} ฉบับ · ขั้นตอนล่าสุด {continueStep} จาก 6</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link className="btn-cta min-h-11" to={`/hr/doc-kit/wizard/${continueStep}`}><PenLine className="h-4 w-4" /> ทำต่อ</Link>
            <Link className="btn-secondary min-h-11" to="/hr/doc-kit/select"><ListChecks className="h-4 w-4" /> เปลี่ยนรายการเอกสาร</Link>
            <Link className="btn-secondary min-h-11" to="/hr/documents"><FileText className="h-4 w-4" /> ดูเอกสารทั้งหมด</Link>
          </div>
        </section>

        {latestBatch ? <BatchProgress batch={latestBatch} /> : (
          <EmptyState
            icon={Plus}
            title="ยังไม่ได้สร้างชุดเอกสาร"
            detail="ตอบข้อมูลให้ครบ แล้วระบบจะสร้างร่างเอกสารตามรายการที่เลือก"
            action={{ label: 'ทำขั้นตอนต่อ', onClick: () => navigate(`/hr/doc-kit/wizard/${continueStep}`) }}
          />
        )}

        {documents.length > 0 && (
          <section className="card overflow-hidden">
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 p-4">
              <h2 className="font-semibold">เอกสารล่าสุด</h2>
              <Link className="inline-flex min-h-11 items-center text-sm font-medium text-brand-700" to="/hr/documents">ดูทั้งหมด <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </div>
            <ul className="divide-y divide-gray-100">
              {[...documents].sort((a, b) => b.created_at.localeCompare(a.created_at)).slice(0, 5).map((document) => (
                <li key={document.id}>
                  <Link to={`/hr/documents/${encodeURIComponent(document.id)}`} className="flex min-h-11 items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50">
                    <span className="min-w-0"><strong className="block truncate text-sm">{document.title}</strong><span className="text-xs text-gray-500">ฉบับที่ {document.doc_version} · มีผล {dateLabel(document.effective_from)}</span></span>
                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700">{STATUS_LABEL[document.status]}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </AdminLayout>
  );
}
